import { useState, useEffect, useMemo, useRef } from "react";
import {
  threadsGetThread,
  messagesGetMessages,
  rulesListRules,
  knowledgeListKnowledgeItems,
  evaluationsGetThreadScorecard,
  evaluationsListEvaluations,
  commentsListComments,
  feedbackBatchesListFeedbackBatches,
  runsListRuns,
  guardrailsListItems,
} from "../../gen";
import type {
  ThreadResponse,
  MessageResponse,
  RuleResponse,
  KnowledgeItemResponse,
  GuardrailItemResponse,
  ThreadScorecardResponse,
  EvaluationResponse,
  CommentResponse,
  BatchResponse,
} from "../../gen/models";
import {
  getAgentInfo,
  extractThreadGovernanceItems,
  extractListItems,
} from "./threadUtils";

export interface TelemetryBreakdownItem {
  key: string;
  label: string;
  agentType: string;
  entryPoint: string;
  runsCount: number;
  tokens: number;
  tokensIn: number;
  tokensOut: number;
  costUsd: number;
  avgDurationMs: number;
}

export interface TelemetrySummary {
  totalTokens: number;
  totalTokensIn: number;
  totalTokensOut: number;
  totalCachedTokens: number;
  totalCostUsd: number;
  avgLatencyMs: number;
  runsCount: number;
  breakdown: TelemetryBreakdownItem[];
}

interface UseThreadDataOptions {
  threadId?: string;
  onAgentReplied?: () => void;
}

export function useThreadData({ threadId, onAgentReplied }: UseThreadDataOptions) {
  const [thread, setThread] = useState<ThreadResponse | null>(null);
  const threadRef = useRef<ThreadResponse | null>(null);
  threadRef.current = thread;

  const fetchSeqRef = useRef(0);
  const deletedCommentIdsRef = useRef<Set<string>>(new Set());

  const registerDeletedCommentId = (id: string) => {
    deletedCommentIdsRef.current.add(id);
  };

  const [messages, setMessages] = useState<MessageResponse[]>([]);
  const [rulesList, setRulesList] = useState<RuleResponse[]>([]);
  const [guardrailsList, setGuardrailsList] = useState<GuardrailItemResponse[]>([]);
  const [knowledgeList, setKnowledgeList] = useState<KnowledgeItemResponse[]>([]);
  const [scorecard, setScorecard] = useState<ThreadScorecardResponse | null>(null);
  const [evaluations, setEvaluations] = useState<EvaluationResponse[]>([]);
  const [comments, setComments] = useState<CommentResponse[]>([]);
  const [commentBatchFilter, setCommentBatchFilter] = useState<string>("all");
  const [activeBatches, setActiveBatches] = useState<BatchResponse[]>([]);
  const [allBatches, setAllBatches] = useState<BatchResponse[]>([]);
  const [activeBatchId, setActiveBatchId] = useState<string>("");
  const [telemetrySummary, setTelemetrySummary] = useState<TelemetrySummary>({
    totalTokens: 0,
    totalTokensIn: 0,
    totalTokensOut: 0,
    totalCachedTokens: 0,
    totalCostUsd: 0,
    avgLatencyMs: 0,
    runsCount: 0,
    breakdown: [],
  });
  const [runsMap, setRunsMap] = useState<Map<string, any>>(new Map());
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sortMessagesChronologically = (msgs: MessageResponse[]) =>
    [...msgs].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

  const loadThreadData = async (isBackground = false) => {
    if (!threadId) return;
    const isInitial = !threadRef.current && !isBackground;
    if (isInitial) {
      setLoading(true);
      setError(null);
    } else {
      setIsRefreshing(true);
    }
    const seq = ++fetchSeqRef.current;
    try {
      const [tRes, mRes, rulesRes, kRes, scRes, evalsRes, commsRes, batchesRes, runsRes, guardrailsRes] = await Promise.all([
        threadsGetThread(threadId),
        messagesGetMessages(threadId),
        rulesListRules({ limit: 200 }).catch(() => ({ data: [] })),
        knowledgeListKnowledgeItems({ limit: 200 }).catch(() => ({ data: [] })),
        evaluationsGetThreadScorecard(threadId).catch(() => ({ data: null })),
        evaluationsListEvaluations({ thread_id: threadId, include_test: true, limit: 200 }).catch(() => ({ data: [] })),
        commentsListComments({ thread_id: threadId, limit: 200 }).catch(() => ({ data: [] })),
        feedbackBatchesListFeedbackBatches({ limit: 200 }).catch(() => ({ data: [] })),
        runsListRuns({ thread_id: threadId, limit: 200 }).catch(() => ({ data: [] })),
        guardrailsListItems().catch(() => ({ data: [] })),
      ]);

      if (seq !== fetchSeqRef.current) {
        return;
      }

      const threadData = (tRes as any).data || null;
      setThread(threadData);
      threadRef.current = threadData;

      const incomingMsgs = sortMessagesChronologically((mRes as any).data?.messages || []);
      setMessages((prev) => {
        const pendingOptimistic = prev.filter(
          (m) =>
            m.id.startsWith("temp-") &&
            !incomingMsgs.some((im) => im.channel_message_ts === m.channel_message_ts)
        );
        if (pendingOptimistic.length === 0) {
          return incomingMsgs;
        }
        return sortMessagesChronologically([...incomingMsgs, ...pendingOptimistic]);
      });

      setRulesList(extractListItems<RuleResponse>(rulesRes));
      setGuardrailsList(extractListItems<GuardrailItemResponse>(guardrailsRes));
      setKnowledgeList(extractListItems<KnowledgeItemResponse>(kRes));
      setScorecard((scRes as any).data || null);
      setEvaluations(extractListItems<EvaluationResponse>(evalsRes));

      const incomingComments = extractListItems<CommentResponse>(commsRes);
      setComments((prev) => {
        const incomingMap = new Map(incomingComments.map((c) => [c.id, c]));
        const now = Date.now();

        // Retain pending optimistic comments or recent comments that the server snapshot missed
        const retainedPending = prev.filter((prevComment) => {
          if (incomingMap.has(prevComment.id)) {
            return false;
          }
          if (deletedCommentIdsRef.current.has(prevComment.id)) {
            return false;
          }
          if (prevComment.id.startsWith("temp-")) {
            return true;
          }
          const createdTime = new Date(prevComment.created_at).getTime();
          if (!isNaN(createdTime) && now - createdTime < 30000) {
            return true;
          }
          return false;
        });

        // For comments that exist in both, prefer local if locally updated more recently
        const mergedIncoming = incomingComments.map((inc) => {
          const local = prev.find((p) => p.id === inc.id);
          if (local && local.updated_at && inc.updated_at) {
            const localUp = new Date(local.updated_at).getTime();
            const incUp = new Date(inc.updated_at).getTime();
            if (!isNaN(localUp) && !isNaN(incUp) && localUp > incUp) {
              return local;
            }
          }
          return inc;
        });

        if (retainedPending.length === 0) {
          return mergedIncoming;
        }

        return [...mergedIncoming, ...retainedPending];
      });

      const bList = extractListItems<BatchResponse>(batchesRes).sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setAllBatches(bList);
      const openBatches = bList.filter((b) => b.status === "collecting");
      setActiveBatches(openBatches);
      if (openBatches.length > 0 && !activeBatchId) {
        setActiveBatchId(openBatches[0].id);
      }

      // Compute telemetry summary and breakdown for runs belonging to this thread
      const threadRuns = extractListItems<any>(runsRes).filter(
        (r) => r.thread_id === threadId
      );
      let totalTokens = 0;
      let totalTokensIn = 0;
      let totalTokensOut = 0;
      let totalCachedTokens = 0;
      let totalCostUsd = 0;
      let totalLatency = 0;
      let runsWithLatency = 0;

      const rMap = new Map<string, any>();

      const groupMap = new Map<
        string,
        {
          key: string;
          label: string;
          agentType: string;
          entryPoint: string;
          runsCount: number;
          tokens: number;
          tokensIn: number;
          tokensOut: number;
          costUsd: number;
          durationSum: number;
          durationRuns: number;
        }
      >();

      for (const r of threadRuns) {
        rMap.set(r.id, r);
        if (r.trigger_payload?.message_id) {
          rMap.set(r.trigger_payload.message_id, r);
        }
        if (r.result_payload?.message_id) {
          rMap.set(r.result_payload.message_id, r);
        }

        const tel = r.telemetry || {};
        const runTokens = Number(tel.total_tokens || 0);
        const inTok = Number(tel.tokens_in ?? tel.input_tokens ?? 0);
        const outTok = Number(tel.tokens_out ?? tel.output_tokens ?? 0);
        const cachedTok = Number(tel.cached_tokens ?? tel.prompt_tokens_cached ?? 0);
        const runCost = Number(tel.cost_usd || tel.estimated_cost_usd || 0);

        totalTokens += runTokens;
        totalTokensIn += inTok;
        totalTokensOut += outTok;
        totalCachedTokens += cachedTok;
        totalCostUsd += runCost;

        const durationMs =
          tel.duration_ms !== undefined && tel.duration_ms !== null
            ? Number(tel.duration_ms)
            : tel.duration_seconds !== undefined && tel.duration_seconds !== null
            ? Math.round(Number(tel.duration_seconds) * 1000)
            : tel.latency_ms !== undefined && tel.latency_ms !== null
            ? Number(tel.latency_ms)
            : 0;

        if (durationMs > 0) {
          totalLatency += durationMs;
          runsWithLatency++;
        }

        const agentType = r.agent_type || "agent";
        const entryPoint = r.entry_point || "run";
        const groupKey = `${agentType}:${entryPoint}`;

        let label = `${agentType.charAt(0).toUpperCase() + agentType.slice(1)} (${entryPoint})`;
        if (agentType === "feedback" || entryPoint === "online_evaluator") {
          label = "Turn Evaluation (Feedback Agent)";
        } else if (entryPoint === "turn") {
          label = `Agent Turn (${agentType})`;
        } else if (entryPoint === "simulate") {
          label = `Simulation (${agentType})`;
        }

        const existing = groupMap.get(groupKey) || {
          key: groupKey,
          label,
          agentType,
          entryPoint,
          runsCount: 0,
          tokens: 0,
          tokensIn: 0,
          tokensOut: 0,
          costUsd: 0,
          durationSum: 0,
          durationRuns: 0,
        };

        existing.runsCount += 1;
        existing.tokens += runTokens;
        existing.tokensIn += inTok;
        existing.tokensOut += outTok;
        existing.costUsd += runCost;
        if (durationMs > 0) {
          existing.durationSum += durationMs;
          existing.durationRuns += 1;
        }

        groupMap.set(groupKey, existing);
      }

      setRunsMap(rMap);

      const breakdown: TelemetryBreakdownItem[] = Array.from(groupMap.values())
        .map((g) => ({
          key: g.key,
          label: g.label,
          agentType: g.agentType,
          entryPoint: g.entryPoint,
          runsCount: g.runsCount,
          tokens: g.tokens,
          tokensIn: g.tokensIn,
          tokensOut: g.tokensOut,
          costUsd: g.costUsd,
          avgDurationMs: g.durationRuns > 0 ? Math.round(g.durationSum / g.durationRuns) : 0,
        }))
        .sort((a, b) => b.costUsd - a.costUsd);

      setTelemetrySummary({
        totalTokens,
        totalTokensIn,
        totalTokensOut,
        totalCachedTokens,
        totalCostUsd,
        avgLatencyMs: runsWithLatency > 0 ? Math.round(totalLatency / runsWithLatency) : 0,
        runsCount: threadRuns.length,
        breakdown,
      });

      // Clear agent waiting state once assistant reply appears as the latest message
      const latestMsg = incomingMsgs.length > 0 ? incomingMsgs[incomingMsgs.length - 1] : null;
      if (latestMsg && (latestMsg.sender_role === "assistant" || latestMsg.sender_role === "agent")) {
        onAgentReplied?.();
      }
    } catch (err: any) {
      if (isInitial) {
        setError(err?.message || "Failed to load thread details");
      }
    } finally {
      if (isInitial) {
        setLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  };

  useEffect(() => {
    fetchSeqRef.current++;
    deletedCommentIdsRef.current.clear();
    setThread(null);
    threadRef.current = null;
    setMessages([]);
    loadThreadData(false);

    // Auto-refresh conversation every 3 seconds while active
    const interval = setInterval(() => {
      loadThreadData(true);
    }, 3000);

    return () => clearInterval(interval);
  }, [threadId]);

  // Active batch object
  const activeBatch = useMemo(
    () => activeBatches.find((b) => b.id === activeBatchId),
    [activeBatches, activeBatchId]
  );

  // Scoped rules, guardrails & knowledge referenced in this conversation thread
  const { referencedRules, referencedKnowledge, referencedGuardrails } = useMemo(
    () => extractThreadGovernanceItems(messages, rulesList, knowledgeList, guardrailsList),
    [messages, rulesList, knowledgeList, guardrailsList]
  );

  const agentInfo = getAgentInfo(thread?.agent_type, thread?.agent_name || undefined);
  const compositeScore = scorecard?.aggregate_score ?? thread?.aggregate_score;
  const isFlagged = scorecard?.is_flagged || thread?.is_flagged || thread?.status === "flagged";
  const hasHallucination = scorecard?.hallucination_detected || thread?.hallucination_detected;

  return {
    thread,
    setThread,
    messages,
    setMessages,
    rulesList,
    knowledgeList,
    scorecard,
    evaluations,
    comments,
    setComments,
    registerDeletedCommentId,
    commentBatchFilter,
    setCommentBatchFilter,
    activeBatches,
    setActiveBatches,
    allBatches,
    setAllBatches,
    activeBatchId,
    setActiveBatchId,
    telemetrySummary,
    runsMap,
    loading,
    isRefreshing,
    error,
    loadThreadData,
    sortMessagesChronologically,
    activeBatch,
    referencedRules,
    referencedKnowledge,
    referencedGuardrails,
    guardrailsList,
    agentInfo,
    compositeScore,
    isFlagged,
    hasHallucination,
  };
}
